import React, { useCallback, useEffect, useRef, useState } from "react";
import { Card } from "primereact/card";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { InputSwitch } from "primereact/inputswitch";
import { Toast } from "primereact/toast";
import { ProgressSpinner } from "primereact/progressspinner";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { Dialog } from "primereact/dialog";
import {
  dictionaryApi,
  DictionaryAllowlistEntry,
  DictionaryBlocklistEntry,
} from "../../../api/metadataSpellcheckApi";
import "./DictionarySettingsTab.css";
import { FULL_PAGE_TABLE_SCROLL_HEIGHT } from "../../../config/tableLayout";

const DictionarySettingsTab = () => {
  const toast = useRef<Toast>(null);

  // Allowlist state
  const [allowlist, setAllowlist] = useState<DictionaryAllowlistEntry[]>([]);
  const [allowlistLoading, setAllowlistLoading] = useState(false);
  const [newAllowlistPhrase, setNewAllowlistPhrase] = useState("");
  const [newAllowlistCaseSensitive, setNewAllowlistCaseSensitive] = useState(false);
  const [addingAllowlist, setAddingAllowlist] = useState(false);

  // Allowlist edit state
  const [editingAllowlistEntry, setEditingAllowlistEntry] = useState<DictionaryAllowlistEntry | null>(null);
  const [editAllowlistCaseSensitive, setEditAllowlistCaseSensitive] = useState(false);
  const [savingAllowlistEdit, setSavingAllowlistEdit] = useState(false);

  // Blocklist state
  const [blocklist, setBlocklist] = useState<DictionaryBlocklistEntry[]>([]);
  const [blocklistLoading, setBlocklistLoading] = useState(false);
  const [newBlocklistPhrase, setNewBlocklistPhrase] = useState("");
  const [newBlocklistSubstitute, setNewBlocklistSubstitute] = useState("");
  const [newBlocklistCaseSensitive, setNewBlocklistCaseSensitive] = useState(false);
  const [addingBlocklist, setAddingBlocklist] = useState(false);

  // Blocklist edit state
  const [editingEntry, setEditingEntry] = useState<DictionaryBlocklistEntry | null>(null);
  const [editSubstitute, setEditSubstitute] = useState("");
  const [editCaseSensitive, setEditCaseSensitive] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);

  const loadAllowlist = useCallback(async () => {
    setAllowlistLoading(true);
    try {
      const { entries } = await dictionaryApi.getAllowlist();
      setAllowlist(entries);
    } catch {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to load allowlist",
        life: 4000,
      });
    } finally {
      setAllowlistLoading(false);
    }
  }, []);

  const loadBlocklist = useCallback(async () => {
    setBlocklistLoading(true);
    try {
      const { entries } = await dictionaryApi.getBlocklist();
      setBlocklist(entries);
    } catch {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to load blocklist",
        life: 4000,
      });
    } finally {
      setBlocklistLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAllowlist();
    loadBlocklist();
  }, [loadAllowlist, loadBlocklist]);

  const handleAddAllowlist = async () => {
    const phrase = newAllowlistPhrase.trim();
    if (!phrase) return;
    setAddingAllowlist(true);
    try {
      await dictionaryApi.addAllowlistEntry({
        phrase,
        caseSensitive: newAllowlistCaseSensitive,
      });
      setNewAllowlistPhrase("");
      setNewAllowlistCaseSensitive(false);
      toast.current?.show({
        severity: "success",
        summary: "Added",
        detail: `"${phrase}" added to allowlist`,
        life: 3000,
      });
      await loadAllowlist();
    } catch {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to add allowlist entry",
        life: 4000,
      });
    } finally {
      setAddingAllowlist(false);
    }
  };

  const handleEditAllowlist = (entry: DictionaryAllowlistEntry) => {
    setEditingAllowlistEntry(entry);
    setEditAllowlistCaseSensitive(entry.caseSensitive);
  };

  const handleSaveAllowlistEdit = async () => {
    if (!editingAllowlistEntry) return;
    setSavingAllowlistEdit(true);
    try {
      await dictionaryApi.updateAllowlistEntry(editingAllowlistEntry.id, {
        caseSensitive: editAllowlistCaseSensitive,
      });
      toast.current?.show({
        severity: "success",
        summary: "Updated",
        detail: `"${editingAllowlistEntry.phrase}" updated`,
        life: 3000,
      });
      setEditingAllowlistEntry(null);
      await loadAllowlist();
    } catch {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to update allowlist entry",
        life: 4000,
      });
    } finally {
      setSavingAllowlistEdit(false);
    }
  };

  const handleDeleteAllowlist = (entry: DictionaryAllowlistEntry) => {
    confirmDialog({
      message: `Remove "${entry.phrase}" from the allowlist?`,
      header: "Confirm removal",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      accept: async () => {
        try {
          await dictionaryApi.deleteAllowlistEntry(entry.id);
          toast.current?.show({
            severity: "success",
            summary: "Removed",
            detail: `"${entry.phrase}" removed`,
            life: 3000,
          });
          await loadAllowlist();
        } catch {
          toast.current?.show({
            severity: "error",
            summary: "Error",
            detail: "Failed to remove allowlist entry",
            life: 4000,
          });
        }
      },
    });
  };

  const handleAddBlocklist = async () => {
    const phrase = newBlocklistPhrase.trim();
    if (!phrase) return;
    setAddingBlocklist(true);
    try {
      await dictionaryApi.addBlocklistEntry({
        phrase,
        substitute: newBlocklistSubstitute.trim() || null,
        caseSensitive: newBlocklistCaseSensitive,
      });
      setNewBlocklistPhrase("");
      setNewBlocklistSubstitute("");
      setNewBlocklistCaseSensitive(false);
      toast.current?.show({
        severity: "success",
        summary: "Added",
        detail: `"${phrase}" added to blocklist`,
        life: 3000,
      });
      await loadBlocklist();
    } catch {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to add blocklist entry",
        life: 4000,
      });
    } finally {
      setAddingBlocklist(false);
    }
  };

  const handleEditBlocklist = (entry: DictionaryBlocklistEntry) => {
    setEditingEntry(entry);
    setEditSubstitute(entry.substitute ?? "");
    setEditCaseSensitive(entry.caseSensitive);
  };

  const handleSaveEdit = async () => {
    if (!editingEntry) return;
    setSavingEdit(true);
    try {
      await dictionaryApi.updateBlocklistEntry(editingEntry.id, {
        substitute: editSubstitute.trim() || null,
        caseSensitive: editCaseSensitive,
      });
      toast.current?.show({
        severity: "success",
        summary: "Updated",
        detail: `"${editingEntry.phrase}" updated`,
        life: 3000,
      });
      setEditingEntry(null);
      await loadBlocklist();
    } catch {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to update blocklist entry",
        life: 4000,
      });
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDeleteBlocklist = (entry: DictionaryBlocklistEntry) => {
    confirmDialog({
      message: `Remove "${entry.phrase}" from the blocklist?`,
      header: "Confirm removal",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      accept: async () => {
        try {
          await dictionaryApi.deleteBlocklistEntry(entry.id);
          toast.current?.show({
            severity: "success",
            summary: "Removed",
            detail: `"${entry.phrase}" removed`,
            life: 3000,
          });
          await loadBlocklist();
        } catch {
          toast.current?.show({
            severity: "error",
            summary: "Error",
            detail: "Failed to remove blocklist entry",
            life: 4000,
          });
        }
      },
    });
  };

  const formatDate = (iso: string) => {
    try {
      return new Date(iso).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return iso;
    }
  };

  return (
    <div className="dictionary-settings-tab">
      <Toast ref={toast} />
      <ConfirmDialog />

      <div className="dictionary-settings-grid">
        {/* Allowlist */}
        <Card>
          <div className="dictionary-settings-section-head">
            <h3 className="dictionary-settings-section-title">
              <i className="pi pi-check-circle dictionary-settings-icon-allow" />
              Allowlist
            </h3>
            <p className="dictionary-settings-section-desc">
              Known brand and character names that the spellchecker should never flag as errors.
              Entries here are also added to the Gemini prompt to prevent AI corrections.
              Use <strong>case-sensitive</strong> for names that must appear with exact capitalisation
              (e.g. T.A.B.B.S). Leave it off for brand names where any casing is fine (e.g. Blippi / blippi).
            </p>
          </div>

          <div className="dictionary-settings-add-form">
            <div className="dictionary-settings-add-form-row-aligned">
              <InputText
                value={newAllowlistPhrase}
                onChange={(e) => setNewAllowlistPhrase(e.target.value)}
                placeholder="e.g. Blippi"
                className="dictionary-settings-field-flex"
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleAddAllowlist();
                }}
                disabled={addingAllowlist}
              />
              <span className="dictionary-settings-inline-label">Case-sensitive</span>
              <InputSwitch
                checked={newAllowlistCaseSensitive}
                onChange={(e) => setNewAllowlistCaseSensitive(e.value ?? false)}
                disabled={addingAllowlist}
              />
              <Button
                label="Add"
                icon="pi pi-plus"
                onClick={handleAddAllowlist}
                disabled={!newAllowlistPhrase.trim() || addingAllowlist}
                loading={addingAllowlist}
                size="small"
              />
            </div>
          </div>

          {allowlistLoading ? (
            <div className="dictionary-settings-spinner-wrap">
              <ProgressSpinner style={{ width: "40px", height: "40px" }} />
            </div>
          ) : (
            <DataTable
              value={allowlist}
              size="small"
              emptyMessage="No allowlist entries yet."
              scrollable
              scrollHeight={FULL_PAGE_TABLE_SCROLL_HEIGHT}
            >
              <Column field="phrase" header="Phrase" style={{ flex: 1 }} />
              <Column
                field="caseSensitive"
                header="Case"
                body={(row: DictionaryAllowlistEntry) => (
                  <span
                    className={
                      row.caseSensitive
                        ? "dictionary-settings-case-badge-exact"
                        : "dictionary-settings-case-badge-any"
                    }
                  >
                    {row.caseSensitive ? "exact" : "any"}
                  </span>
                )}
                style={{ width: "60px", textAlign: "center" }}
              />
              <Column
                field="addedAt"
                header="Added"
                body={(row: DictionaryAllowlistEntry) => formatDate(row.addedAt)}
                style={{ width: "110px" }}
              />
              <Column
                field="addedBy"
                header="By"
                body={(row: DictionaryAllowlistEntry) => (
                  <span className="dictionary-settings-muted-cell">{row.addedBy}</span>
                )}
                style={{ width: "140px" }}
              />
              <Column
                body={(row: DictionaryAllowlistEntry) => (
                  <div className="dictionary-settings-row-actions">
                    <Button
                      icon="pi pi-pencil"
                      text
                      size="small"
                      onClick={() => handleEditAllowlist(row)}
                      tooltip="Edit case sensitivity"
                      tooltipOptions={{ position: "left" }}
                    />
                    <Button
                      icon="pi pi-trash"
                      severity="danger"
                      text
                      size="small"
                      onClick={() => handleDeleteAllowlist(row)}
                      tooltip="Remove"
                      tooltipOptions={{ position: "left" }}
                    />
                  </div>
                )}
                style={{ width: "90px", textAlign: "center" }}
              />
            </DataTable>
          )}
        </Card>

        {/* Blocklist */}
        <Card>
          <div className="dictionary-settings-section-head">
            <h3 className="dictionary-settings-section-title">
              <i className="pi pi-ban dictionary-settings-icon-block" />
              Blocklist
            </h3>
            <p className="dictionary-settings-section-desc">
              Prohibited phrases. When detected in a title, the substitute (if set) is suggested
              for review. Titles with no substitute are flagged for manual review.
              Longer phrases are always matched before shorter ones.
            </p>
          </div>

          <div className="dictionary-settings-add-form">
            <div className="dictionary-settings-add-form-row">
              <InputText
                value={newBlocklistPhrase}
                onChange={(e) => setNewBlocklistPhrase(e.target.value)}
                placeholder="Prohibited phrase e.g. World Cup"
                className="dictionary-settings-field-flex"
                disabled={addingBlocklist}
              />
            </div>
            <div className="dictionary-settings-add-form-row-aligned">
              <InputText
                value={newBlocklistSubstitute}
                onChange={(e) => setNewBlocklistSubstitute(e.target.value)}
                placeholder="Substitute (optional) e.g. Soccer Match"
                className="dictionary-settings-field-flex"
                disabled={addingBlocklist}
              />
              <span className="dictionary-settings-inline-label">Case-sensitive</span>
              <InputSwitch
                checked={newBlocklistCaseSensitive}
                onChange={(e) => setNewBlocklistCaseSensitive(e.value ?? false)}
                disabled={addingBlocklist}
              />
              <Button
                label="Add"
                icon="pi pi-plus"
                onClick={handleAddBlocklist}
                disabled={!newBlocklistPhrase.trim() || addingBlocklist}
                loading={addingBlocklist}
                size="small"
              />
            </div>
          </div>

          {blocklistLoading ? (
            <div className="dictionary-settings-spinner-wrap">
              <ProgressSpinner style={{ width: "40px", height: "40px" }} />
            </div>
          ) : (
            <DataTable
              value={blocklist}
              size="small"
              emptyMessage="No blocklist entries yet."
              scrollable
              scrollHeight={FULL_PAGE_TABLE_SCROLL_HEIGHT}
            >
              <Column field="phrase" header="Phrase" style={{ minWidth: "120px" }} />
              <Column
                field="substitute"
                header="Substitute"
                body={(row: DictionaryBlocklistEntry) =>
                  row.substitute ? (
                    <span>{row.substitute}</span>
                  ) : (
                    <span className="dictionary-settings-substitute-empty">none — manual review</span>
                  )
                }
                style={{ minWidth: "140px" }}
              />
              <Column
                field="caseSensitive"
                header="Case"
                body={(row: DictionaryBlocklistEntry) => (
                  <span
                    className={
                      row.caseSensitive
                        ? "dictionary-settings-case-badge-exact"
                        : "dictionary-settings-case-badge-any"
                    }
                  >
                    {row.caseSensitive ? "exact" : "any"}
                  </span>
                )}
                style={{ width: "60px", textAlign: "center" }}
              />
              <Column
                field="addedAt"
                header="Added"
                body={(row: DictionaryBlocklistEntry) => formatDate(row.addedAt)}
                style={{ width: "100px" }}
              />
              <Column
                body={(row: DictionaryBlocklistEntry) => (
                  <div className="dictionary-settings-row-actions">
                    <Button
                      icon="pi pi-pencil"
                      text
                      size="small"
                      onClick={() => handleEditBlocklist(row)}
                      tooltip="Edit substitute / case"
                      tooltipOptions={{ position: "left" }}
                    />
                    <Button
                      icon="pi pi-trash"
                      severity="danger"
                      text
                      size="small"
                      onClick={() => handleDeleteBlocklist(row)}
                      tooltip="Remove"
                      tooltipOptions={{ position: "left" }}
                    />
                  </div>
                )}
                style={{ width: "90px", textAlign: "center" }}
              />
            </DataTable>
          )}
        </Card>
      </div>

      {/* Allowlist edit dialog */}
      <Dialog
        header={
          editingAllowlistEntry
            ? `Edit: "${editingAllowlistEntry.phrase}"`
            : "Edit allowlist entry"
        }
        visible={editingAllowlistEntry !== null}
        style={{ width: "420px" }}
        onHide={() => !savingAllowlistEdit && setEditingAllowlistEntry(null)}
        footer={
          <div className="dictionary-settings-dialog-footer">
            <Button
              label="Cancel"
              text
              disabled={savingAllowlistEdit}
              onClick={() => setEditingAllowlistEntry(null)}
            />
            <Button
              label="Save"
              icon="pi pi-check"
              loading={savingAllowlistEdit}
              disabled={savingAllowlistEdit}
              onClick={() => void handleSaveAllowlistEdit()}
            />
          </div>
        }
      >
        {editingAllowlistEntry && (
          <div className="dictionary-settings-dialog-body">
            <div>
              <label className="dictionary-settings-field-label">
                Phrase (read-only — delete and re-add to change)
              </label>
              <InputText
                value={editingAllowlistEntry.phrase}
                disabled
                className="dictionary-settings-field-input-readonly"
              />
            </div>
            <div className="dictionary-settings-switch-row">
              <InputSwitch
                checked={editAllowlistCaseSensitive}
                onChange={(e) => setEditAllowlistCaseSensitive(e.value ?? false)}
                disabled={savingAllowlistEdit}
              />
              <span className="dictionary-settings-switch-help">
                Case-sensitive matching
                <span className="dictionary-settings-switch-hint">
                  {editAllowlistCaseSensitive
                    ? "Only exact casing is allowlisted (e.g. \"T.A.B.B.S\" not \"t.a.b.b.s\")"
                    : "Any casing is allowlisted (e.g. \"Blippi\", \"blippi\", \"BLIPPI\")"}
                </span>
              </span>
            </div>
          </div>
        )}
      </Dialog>

      {/* Blocklist edit dialog */}
      <Dialog
        header={editingEntry ? `Edit: "${editingEntry.phrase}"` : "Edit blocklist entry"}
        visible={editingEntry !== null}
        style={{ width: "420px" }}
        onHide={() => !savingEdit && setEditingEntry(null)}
        footer={
          <div className="dictionary-settings-dialog-footer">
            <Button
              label="Cancel"
              text
              disabled={savingEdit}
              onClick={() => setEditingEntry(null)}
            />
            <Button
              label="Save"
              icon="pi pi-check"
              loading={savingEdit}
              disabled={savingEdit}
              onClick={() => void handleSaveEdit()}
            />
          </div>
        }
      >
        {editingEntry && (
          <div className="dictionary-settings-dialog-body">
            <div>
              <label className="dictionary-settings-field-label">
                Phrase (read-only — delete and re-add to change)
              </label>
              <InputText
                value={editingEntry.phrase}
                disabled
                className="dictionary-settings-field-input-readonly"
              />
            </div>
            <div>
              <label className="dictionary-settings-field-label">
                Substitute (leave blank for "needs manual review")
              </label>
              <InputText
                value={editSubstitute}
                onChange={(e) => setEditSubstitute(e.target.value)}
                placeholder="e.g. Soccer Match"
                className="dictionary-settings-field-input-full"
                disabled={savingEdit}
                autoFocus
              />
            </div>
            <div className="dictionary-settings-switch-row">
              <InputSwitch
                checked={editCaseSensitive}
                onChange={(e) => setEditCaseSensitive(e.value ?? false)}
                disabled={savingEdit}
              />
              <span className="dictionary-settings-switch-help">
                Case-sensitive matching
                <span className="dictionary-settings-switch-hint">
                  {editCaseSensitive
                    ? "Only exact case matches (e.g. \"FIFA\" won't match \"fifa\")"
                    : "Matches any casing (e.g. \"FIFA\", \"Fifa\", \"fifa\")"}
                </span>
              </span>
            </div>
          </div>
        )}
      </Dialog>

      <Card className="dictionary-settings-info-card">
        <div className="dictionary-settings-info-row">
          <i className="pi pi-info-circle dictionary-settings-info-icon" />
          <div className="dictionary-settings-info-text">
            <strong>Note:</strong> Changes here take effect on the next worker run. The worker caches the
            dictionary at startup — already-running instances will pick up changes on their next cold start.
            Blocklist phrases with no substitute will appear in the review queue as{" "}
            <em>Needs Manual Review</em> so a reviewer can supply a corrected title.
          </div>
        </div>
      </Card>
    </div>
  );
};

export default DictionarySettingsTab;
